import axios from "./axios";

/**
 * 搜索航班操作
 * @param {Object} params - 搜索参数
 * @param {string} params.keyword - 搜索关键词，如航班号
 * @param {number} params.limit - 返回结果数量限制
 * @returns {Promise} 航班数据
 */
export const searchFlightOperations = async (params = {}) => {
    try {
        const response = await axios.get("/v1/flight-operation/search", { params });
        return response;
    } catch (error) {
        console.error("搜索航班失败:", error);
        throw error;
    }
};

/**
 * 获取航班历史时间线事件
 * @param {number} flightId - 航班ID
 * @param {Object} params - 查询参数
 * @param {string} params.category - 事件分类筛选 ('all', 'plan', 'transaction', 'communication', 'system')
 * @param {string} params.search_query - 搜索关键词
 * @param {string} params.start_date - 开始时间
 * @param {string} params.end_date - 结束时间
 * @param {number} params.page - 当前页码，从1开始（遵循API_GUIDELINES规范）
 * @param {number} params.page_size - 每页记录数（遵循API_GUIDELINES规范）
 * @param {boolean} params.sort_desc - 是否倒序排列
 * @param {boolean} params.include_business_entity - 是否包含业务主体的所有航班事件（默认true）
 * @returns {Promise} 时间线事件数据，返回格式为 {items: [], total: number}
 */
export const getFlightTimelineEvents = async (flightId, params = {}) => {
    try {
        // 确保使用标准的分页参数
        const apiParams = {
            ...params,
            page: params.page || 1,
            page_size: params.page_size || 50,
            include_business_entity: params.include_business_entity !== undefined ? params.include_business_entity : true
        };
        
        // 移除旧的分页参数（如果存在）
        delete apiParams.skip;
        delete apiParams.limit;
        
        const response = await axios.get(`/v1/flights/${flightId}/timeline`, { params: apiParams });
        return response;
    } catch (error) {
        console.error("获取航班时间线事件失败:", error);
        throw error;
    }
};

/**
 * 获取航班历史时间线事件统计
 * @param {number} flightId - 航班ID
 * @param {boolean} include_business_entity - 是否包含业务主体的所有航班事件（默认true）
 * @returns {Promise} 事件统计数据
 */
export const getFlightTimelineStatistics = async (flightId, include_business_entity = true) => {
    try {
        const response = await axios.get(`/v1/flights/${flightId}/timeline/statistics`, {
            params: { include_business_entity }
        });
        return response;
    } catch (error) {
        console.error("获取航班时间线统计失败:", error);
        throw error;
    }
};

/**
 * 获取航班历史时间线事件详情
 * @param {number} flightId - 航班ID
 * @param {number} eventId - 事件ID
 * @returns {Promise} 事件详情数据
 */
export const getFlightTimelineEventDetail = async (flightId, eventId) => {
    try {
        const response = await axios.get(`/v1/flights/${flightId}/timeline/events/${eventId}`);
        return response;
    } catch (error) {
        console.error("获取航班时间线事件详情失败:", error);
        throw error;
    }
};

/**
 * 获取航班历史时间线可用的事件分类
 * @param {number} flightId - 航班ID
 * @returns {Promise} 事件分类数据
 */
export const getFlightTimelineCategories = async (flightId) => {
    try {
        const response = await axios.get(`/v1/flights/${flightId}/timeline/categories`);
        return response;
    } catch (error) {
        console.error("获取航班时间线分类失败:", error);
        throw error;
    }
};

/**
 * 获取时间线可用的事件分类（通用接口）
 * @returns {Promise} 事件分类数据
 */
export const getTimelineCategories = async () => {
    try {
        const response = await axios.get("/v1/timeline/categories");
        return response;
    } catch (error) {
        console.error("获取时间线分类失败:", error);
        throw error;
    }
};

/**
 * 获取航班变更对比数据
 * @param {number} flightId - 航班ID
 * @returns {Promise} 航班对比数据，包含基线版本和当前版本的对比信息
 */
export const getFlightComparison = async (flightId) => {
    try {
        const response = await axios.get(`/v1/flight-operations/${flightId}/comparison`);
        return response;
    } catch (error) {
        console.error("获取航班对比数据失败:", error);
        throw error;
    }
};

/**
 * 获取航班时间版本历史
 * @param {number} flightId - 航班ID
 * @returns {Promise} 时间版本历史数据，返回每个版本的发布时间和STD/STA
 */
export const getFlightTimeVersions = async (flightId) => {
    try {
        const response = await axios.get(`/v1/flight-operations/${flightId}/time-versions`);
        return response;
    } catch (error) {
        console.error("获取航班时间版本历史失败:", error);
        throw error;
    }
};

export const getCargoReport = async (flightId) => {
    try {
        const response = await axios.get(`/v1/flight-operations/${flightId}/cargo-report`);
        return response;
    } catch (error) {
        console.error("获取载量报告失败:", error);
        throw error;
    }
};

export const getCargoLoads = async (flightId, loadType) => {
    try {
        const response = await axios.get(`/v1/flight-operations/${flightId}/cargo-loads`, {
            params: { load_type: loadType },
        });
        return response;
    } catch (error) {
        console.error("获取装载数据失败:", error);
        throw error;
    }
};

export default {
    searchFlightOperations,
    getFlightTimelineEvents,
    getFlightTimelineStatistics,
    getFlightTimelineEventDetail,
    getFlightTimelineCategories,
    getTimelineCategories,
    getFlightComparison,
    getFlightTimeVersions,
    getCargoReport,
    getCargoLoads,
};